/**
 * Camera → world-XY rectangle resolution for the top-down ortho views the
 * 2D drafting workbench uses. Returns null for every other projection:
 * for perspective or tilted cameras a plane section is not a conservative
 * bound (geometry above/below the plane projects into the rectangle from
 * outside it), so callers must skip culling rather than drop real hits.
 */

import * as THREE from 'three';
import type { AABB } from './quadtree';

/** NDC corners of the full viewport. */
const NDC_CORNERS: readonly [number, number][] = [
    [-1, -1], [1, -1], [1, 1], [-1, 1],
];

export function orthoViewportWorldRect(camera: THREE.Camera): AABB | null {
    if (!(camera instanceof THREE.OrthographicCamera)) return null;
    const dir = new THREE.Vector3();
    camera.getWorldDirection(dir);
    if (Math.abs(dir.z) < 0.999) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [nx, ny] of NDC_CORNERS) {
        const world = new THREE.Vector3(nx, ny, 0).unproject(camera);
        // Top-down ortho: rays are parallel to Z, so the unprojected x/y
        // equals the z=0 plane hit at any depth.
        if (!Number.isFinite(world.x) || !Number.isFinite(world.y)) return null;
        if (world.x < minX) minX = world.x;
        if (world.y < minY) minY = world.y;
        if (world.x > maxX) maxX = world.x;
        if (world.y > maxY) maxY = world.y;
    }
    return [minX, minY, maxX, maxY];
}

/** Equality at drafting precision — pan/zoom jitter must not re-render. */
export function aabbNearEqual(a: AABB, b: AABB, eps = 1e-9): boolean {
    for (let i = 0; i < 4; i++) {
        if (Math.abs(a[i] - b[i]) > eps * Math.max(1, Math.abs(a[i]))) return false;
    }
    return true;
}
